import { createContext, Dispatch, useContext, useReducer } from "react";
import { AssignState } from "./types";

interface AssignIcbtState {
  assignState: AssignState;
  selectedTreatmentId?: string;
}

type Action =
  | { type: "setSelectedTreatmentId"; payload: string | undefined }
  | {
      type: "setAssignState";
      payload: { programIds: string[]; treatmentId?: string };
    }
  | {
      type: "setFeedbackDateForProgram";
      payload: { programId: string; date?: Date };
    }
  | { type: "resetAssignIcbtState" };

const initialAssignState: AssignState = {
  treatmentOrder: [],
  programsToAssign: {},
};

const initialState: AssignIcbtState = {
  selectedTreatmentId: undefined,
  assignState: initialAssignState,
};

function reducer(state: AssignIcbtState, action: Action): AssignIcbtState {
  switch (action.type) {
    case "setSelectedTreatmentId":
      return { ...state, selectedTreatmentId: action.payload };

    case "setAssignState": {
      const { programIds, treatmentId } = action.payload;
      const effectiveTreatmentId =
        treatmentId ?? state.selectedTreatmentId ?? "";

      const prevProgramIds =
        state.assignState.programsToAssign[effectiveTreatmentId] ?? [];

      const newProgramIdsSet = new Set(prevProgramIds);
      for (const programId of programIds) {
        if (newProgramIdsSet.has(programId)) {
          newProgramIdsSet.delete(programId);
        } else {
          newProgramIdsSet.add(programId);
        }
      }
      const newProgramIds = Array.from(newProgramIdsSet);

      let newProgramsToAssign: Record<string, string[]>;
      let newTreatmentOrder: string[];

      if (newProgramIds.length === 0) {
        const { [effectiveTreatmentId]: _removed, ...rest } =
          state.assignState.programsToAssign;
        newProgramsToAssign = rest;
        newTreatmentOrder = state.assignState.treatmentOrder.filter(
          (id) => id !== effectiveTreatmentId,
        );
      } else {
        const isNew = !state.assignState.programsToAssign[effectiveTreatmentId];
        newTreatmentOrder = isNew
          ? [...state.assignState.treatmentOrder, effectiveTreatmentId]
          : state.assignState.treatmentOrder;
        newProgramsToAssign = {
          ...state.assignState.programsToAssign,
          [effectiveTreatmentId]: newProgramIds,
        };
      }

      // Remove feedback dates for toggled-off programs
      let newProgramFeedbackDates = state.assignState.programFeedbackDates;
      if (newProgramFeedbackDates) {
        const toRemove = Object.keys(newProgramFeedbackDates).filter((p) =>
          programIds.includes(p),
        );
        if (toRemove.length > 0) {
          newProgramFeedbackDates = { ...newProgramFeedbackDates };
          for (const p of toRemove) {
            delete newProgramFeedbackDates[p];
          }
        }
      }

      return {
        ...state,
        assignState: {
          treatmentOrder: newTreatmentOrder,
          programsToAssign: newProgramsToAssign,
          programFeedbackDates: newProgramFeedbackDates,
        },
      };
    }

    case "setFeedbackDateForProgram": {
      const { programId, date } = action.payload;
      const prevDates = state.assignState.programFeedbackDates ?? {};

      let newDates: Record<string, Date>;
      if (!date) {
        const { [programId]: _removed, ...rest } = prevDates;
        newDates = rest;
      } else {
        newDates = { ...prevDates, [programId]: new Date(date) };
      }

      return {
        ...state,
        assignState: {
          ...state.assignState,
          programFeedbackDates: newDates,
        },
      };
    }

    case "resetAssignIcbtState":
      return { ...initialState };

    default:
      return state;
  }
}

interface AssignIcbtContextValue {
  state: AssignIcbtState;
  dispatch: Dispatch<Action>;
  totalCountOfProgramsToBeAssigned: number;
}

const AssignIcbtContext = createContext<AssignIcbtContextValue | undefined>(
  undefined,
);

export const AssignIcbtProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const totalCountOfProgramsToBeAssigned = Object.values(
    state.assignState.programsToAssign,
  ).flat().length;

  return (
    <AssignIcbtContext.Provider
      value={{ state, dispatch, totalCountOfProgramsToBeAssigned }}
    >
      {children}
    </AssignIcbtContext.Provider>
  );
};

export const useAssignIcbt = (): AssignIcbtContextValue => {
  const ctx = useContext(AssignIcbtContext);
  if (!ctx) {
    throw new Error("useAssignIcbt must be used within AssignIcbtProvider");
  }
  return ctx;
};
